import { Box, Grid, IconButton, IconButtonProps, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useAudioControls } from "../hooks/use-audio-controls";
import { Dropdown } from "components/dropdown";
import { useChatsContext } from "../../chats-context";
import { DeleteIcon, PhoneIcon } from "@chakra-ui/icons";
import { slimScroll } from "chakra/theme";
import { PlayIcon } from "components/vectors/play-icon";
import { PauseIcon } from "components/vectors/pause-icon";
import { SendIcon2 } from "components/vectors/send-icon";
import { FaMicrophone } from "react-icons/fa";
import { WhatsAppMessagesHelper } from "../hooks/use-whats-app-session-controls/ws-messages-api";

import { sortArrayBy } from "utils/filter-array-by";
import { useAttendanceContext } from "contexts/attendance-context";
import { AnexarDocument } from "./anexar-document";
import {
  useWhatsAppSessionControlsProps,
  WhatsAppSession,
} from "../hooks/use-whats-app-session-controls/types";

interface InputWhatsAppProps {
  chatStatus: useWhatsAppSessionControlsProps["chatStatus"];
  changeWhatsAppSession: useWhatsAppSessionControlsProps["changeWhatsAppSession"];
  whatsAppPhones: useWhatsAppSessionControlsProps["whatsAppPhones"];
  loadingPhones: useWhatsAppSessionControlsProps["loadingPhones"];
  wsSession: useWhatsAppSessionControlsProps["wsSession"];
  currentSession: WhatsAppSession | undefined;
}

export function InputWhatsApp({
  chatStatus,
  changeWhatsAppSession,
  loadingPhones,
  whatsAppPhones,
  wsSession,
  currentSession,
}: InputWhatsAppProps) {
  const textBox = useRef<HTMLDivElement>(null);
  const [messageBody, setMessageBody] = useState("");
  const [selectedFile, setSelectedFile] = useState<File>();
  const { formControl, isReadOnly } = useAttendanceContext();
  const whatsAppPhone = formControl.values.telefoneContatoNormalizado;
  const { selectedAttendance } = useChatsContext();
  const {
    deleteAudio,
    isPaused,
    isUsingAudio,
    pauseAudio,
    playAudio,
    startAudio,
    stopAudio,
  } = useAudioControls();
  const selectedAttendanceId = selectedAttendance?.id || formControl.values.id;
  const whatsAppApi = new WhatsAppMessagesHelper(wsSession);
  const isDisabled =
    chatStatus !== "authenticated" || !whatsAppPhone || isReadOnly;
  const isDropdownDisabled = !selectedAttendanceId || isReadOnly;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearChat = () => {
    textBox.current!.textContent = "";
    setMessageBody("");
  };

  useEffect(() => {
    if (chatStatus === "not-authenticated") {
      clearChat();
    }
  }, [chatStatus]);

  useEffect(() => {
    clearChat();
  }, [selectedAttendanceId]);

  return (
    <>
      <Grid
        w="97%"
        templateColumns={"1fr"}
        gap="10px"
        flexDir="column"
        alignItems="stretch"
        pt="0px"
        width="97%"
        mx="auto"
      >
        <Dropdown
          options={sortArrayBy(whatsAppPhones, "nome", "ASC")}
          onChange={changeWhatsAppSession}
          value={currentSession?.id}
          leftIcon={<PhoneIcon />}
          borderRadius="20px 20px 0px 0px"
          optionValue="id"
          optionLabel="nome"
          isDisabled={isDropdownDisabled}
          bg="#076058"
          _hover={{ bg: "#00a884" }}
          aria-label=""
          h="41px"
          isLoading={loadingPhones}
          placeholder="Selecione um telefone"
          _loading={{
            _hover: { bg: "#076058" },
            opacity: 0.5,
            cursor: "not-allowed",
          }}
          color="#fff"
          _disabled={{
            _hover: { bg: "#076058" },
            opacity: 0.5,
            cursor: "not-allowed",
          }}
          width="100%"
          justifyContent="flex-start"
        >
          <Text mr="auto">
            {currentSession?.nome
              ? currentSession?.nome
              : "Selecione um telefone"}
          </Text>
        </Dropdown>
      </Grid>
      <Grid
        w="97%"
        mx="auto"
        templateColumns={
          isUsingAudio ? "auto 32px 32px 32px" : "32px auto 32px"
        }
        gap="10px"
        // alignItems="center"
        borderRadius="0px 0px 20px 20px"
        py="1"
        px="1"
        bg="#fff"
      >
        {!isUsingAudio && (
          <AnexarDocument
            isUsingAudio={isUsingAudio}
            fileInputRef={fileInputRef}
            isDisabled={isDisabled}
            whatsAppButtonStyles={whatsAppButtonStyles}
            setSelectedFile={setSelectedFile}
            selectedFile={selectedFile}
            fileExtensions={[]}
            wsSession={wsSession}
          />
        )}

        <Box
          ref={textBox}
          p={isUsingAudio ? "6px 15px" : "6px 15px 6px 0px"}
          minH="32px"
          fontSize="14px"
          lineHeight="20px" // <-- ESSENCIAL
          css={slimScroll}
          overflow="auto"
          onKeyUp={(e) => {
            const { textContent } = e.currentTarget;
            const value = textContent || "";
            setMessageBody(value);
            if (value === "") clearChat();
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              whatsAppApi.sendText({ body: value });
              clearChat();
            }
          }}
          onKeyDown={(e) =>
            e.key === "Enter" && !e.shiftKey ? e.preventDefault() : undefined
          }
          whiteSpace="pre-wrap"
          _focus={{ border: 0, outline: "none" }}
          pos="relative"
          role="textbox"
          cursor={!isDisabled ? undefined : "not-allowed"}
          _empty={{
            _hover: { cursor: "text" },
            _before: {
              content:
                chatStatus === "authenticated"
                  ? !whatsAppPhone
                    ? '"Insira o telefone WhatsApp do contato"'
                    : '"Digite uma mensagem"'
                  : '"Sessão WhatsApp não autenticada"',
              color: "#999",
              pointerEvents: "none",
              display: "block",
              height: "20px",
              lineHeight: "20px",
            },
          }}
          maxH="132px"
          contentEditable={!isDisabled}
          opacity={isDisabled ? 0.5 : 1}
        />

        {isUsingAudio ? (
          <>
            <IconButton
              mt="auto"
              icon={<DeleteIcon />}
              borderRadius="full"
              bg="#076058"
              w="32px"
              _hover={{ bg: "#00a884" }}
              aria-label="Excluir áudio"
              onClick={deleteAudio}
              _disabled={{
                _hover: { bg: "#076058" },
                opacity: 0.5,
                cursor: "not-allowed",
              }}
            />
            <IconButton
              mt="auto"
              icon={isPaused ? <PlayIcon color="#EB4E3D" /> : <PauseIcon />}
              borderRadius="full"
              bg="transparent"
              w="32px"
              _hover={{ bg: "transparent" }}
              aria-label="Pausar/Reproduzir áudio"
              color="#EB4E3D"
              border="3px solid #EB4E3D"
              onClick={isPaused ? playAudio : pauseAudio}
              _disabled={{
                _hover: { bg: "#076058" },
                opacity: 0.5,
                cursor: "not-allowed",
              }}
            />
          </>
        ) : null}

        <IconButton
          mt="auto"
          icon={
            messageBody.trim() ? (
              <SendIcon2 />
            ) : isUsingAudio ? (
              <SendIcon2 />
            ) : (
              <FaMicrophone />
            )
          }
          animation={isUsingAudio ? "recording 1.6s linear infinite" : ""}
          aria-label="Enviar mensagem"
          onClick={async () => {
            if (messageBody.trim()) {
              const isSended = whatsAppApi.sendText({
                body: messageBody || "",
              });
              clearChat();
            } else {
              if (isUsingAudio) {
                const { audio } = await stopAudio({ finalize: true });
                if (audio) {
                  await whatsAppApi.sendAudio(audio);
                }
              } else startAudio();
            }
          }}
          isDisabled={isDisabled}
          {...whatsAppButtonStyles}
        />
      </Grid>
    </>
  );
}

const whatsAppButtonStyles: Partial<IconButtonProps> = {
  borderRadius: "full",
  bg: "#076058",
  w: "32px",
  _hover: { bg: "#00a884" },
  _disabled: {
    _hover: { bg: "#076058" },
    opacity: 0.5,
    cursor: "not-allowed",
  },
};
